import React from 'react';
import styled from 'styled-components';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/common/SEO';
import theme from '../../theme';
import cnrExpo2023 from '../../assets/images/news/cnrexpo2023.jpg';
import ceoInterview from '../../assets/images/news/boranka_teknoloji_kurucusu_ve_ceosu_emre_okumusasla_vazgecmem_h18716_07ed8.webp';
import aiCommerce from '../../assets/images/news/yapay_zekayi_ticaretin_ayrilmaz_bir_parcasi_haline_getirmeyi_hedefliyor_h16547_53640.webp';

const NewsDetailSection = styled.section`
  padding: 6rem 0 4rem;
  background-color: ${theme.colors.lightGray};
`;

const NewsDetailContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius};
  overflow: hidden;
  box-shadow: ${theme.boxShadow};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.primary};
  font-weight: 600;
  margin-bottom: 2rem;
  transition: ${theme.transition};
  
  &:before {
    content: '←';
    margin-right: 0.5rem;
  }
  
  &:hover {
    color: ${theme.colors.accent};
    transform: translateX(-5px);
  }
`;

const NewsHeroImage = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 300px;
  }
`;

const NewsTag = styled.span`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: ${theme.fontSizes.small};
  font-weight: 600;
`;

const NewsContent = styled.div`
  padding: 3rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 2rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.5rem;
  }
`;

const NewsDate = styled.div`
  color: ${theme.colors.darkGray};
  font-size: ${theme.fontSizes.small};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  &:before {
    content: '🗓️';
    margin-right: 0.5rem;
  }
`;

const NewsTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  color: ${theme.colors.secondary};
  margin-bottom: 2rem;
  line-height: 1.3;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.fontSizes.xlarge};
  }
`;

const NewsBody = styled.div`
  font-size: ${theme.fontSizes.medium};
  line-height: 1.8;
  color: ${theme.colors.secondary};
  
  p {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: ${theme.fontSizes.xlarge};
    margin: 2rem 0 1rem;
    color: ${theme.colors.secondary};
  }
  
  h3 {
    font-size: ${theme.fontSizes.large};
    margin: 1.5rem 0 1rem;
    color: ${theme.colors.secondary};
  }
  
  ul, ol {
    margin: 1.5rem 0;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
  
  blockquote {
    border-left: 4px solid ${theme.colors.primary};
    padding-left: 1.5rem;
    margin: 2rem 0;
    font-style: italic;
    color: ${theme.colors.darkGray};
  }
  
  strong {
    font-weight: 700;
    color: ${theme.colors.secondary};
  }
`;

const SourceLink = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 2px solid ${theme.colors.lightGray};
  
  p {
    margin-bottom: 1rem;
    color: ${theme.colors.darkGray};
    font-size: ${theme.fontSizes.small};
  }
  
  a {
    display: inline-flex;
    align-items: center;
    color: ${theme.colors.primary};
    font-weight: 600;
    transition: ${theme.transition};
    
    &:after {
      content: '↗';
      margin-left: 0.5rem;
    }
    
    &:hover {
      color: ${theme.colors.accent};
      text-decoration: underline;
    }
  }
`;

const NewsDetailPage = () => {
  const { t } = useTranslation();
  const { lang, slug } = useParams();
  
  // Haber slug'ına göre içerik belirle
  const newsData = {
    'cnr-expo-2023': {
      titleKey: 'news.items.cnrExpo2023.title',
      dateKey: 'news.items.cnrExpo2023.date',
      tagKey: 'news.items.cnrExpo2023.tag',
      image: cnrExpo2023,
      contentKey: 'news.items.cnrExpo2023.content',
      sourceUrl: null
    },
    'boranka-teknoloji-kurucusu-emre-okumus': {
      titleKey: 'news.items.ceoInterview.title',
      dateKey: 'news.items.ceoInterview.date',
      tagKey: 'news.items.ceoInterview.tag',
      image: ceoInterview,
      contentKey: 'news.items.ceoInterview.content',
      sourceUrl: 'https://www.kobi-efor.com.tr/boranka-teknoloji-kurucusu-ve-ceosu-emre-okumusasla-vazgecmem'
    },
    'yapay-zeka-ticaret': {
      titleKey: 'news.items.aiCommerce.title',
      dateKey: 'news.items.aiCommerce.date',
      tagKey: 'news.items.aiCommerce.tag',
      image: aiCommerce,
      contentKey: 'news.items.aiCommerce.content',
      sourceUrl: 'https://www.kobi-efor.com.tr/yapay-zekayi-ticaretin-ayrilmaz-bir-parcasi-haline-getirmeyi-hedefliyor'
    }
  };
  
  const currentNews = newsData[slug];
  
  if (!currentNews) {
    return (
      <NewsDetailSection>
        <div className="container">
          <BackLink to={`/${lang}/news`}>{t('news.backToNews')}</BackLink>
          <p>{t('news.notFound')}</p>
        </div>
      </NewsDetailSection>
    );
  }
  
  return (
    <>
      <SEO 
        titleKey={currentNews.titleKey}
        descriptionKey={`${currentNews.contentKey}.intro`}
      />
      
      <NewsDetailSection>
        <div className="container">
          <BackLink to={`/${lang}/news`}>{t('news.backToNews')}</BackLink>
          
          <NewsDetailContainer>
            <NewsHeroImage>
              <img src={currentNews.image} alt={t(currentNews.titleKey)} loading="lazy" />
              <NewsTag>{t(currentNews.tagKey)}</NewsTag>
            </NewsHeroImage>
            
            <NewsContent>
              <NewsDate>{t(currentNews.dateKey)}</NewsDate>
              <NewsTitle>{t(currentNews.titleKey)}</NewsTitle>
              
              <NewsBody dangerouslySetInnerHTML={{ __html: t(currentNews.contentKey) }} />
              
              {currentNews.sourceUrl && (
                <SourceLink>
                  <p>{t('news.sourceLabel')}</p>
                  <a href={currentNews.sourceUrl} target="_blank" rel="noopener noreferrer">
                    {t('news.readOriginal')}
                  </a>
                </SourceLink>
              )}
            </NewsContent>
          </NewsDetailContainer>
        </div>
      </NewsDetailSection>
    </>
  );
};

export default NewsDetailPage;

